import { useEffect, useMemo, useState } from 'react';
import {
  X, Truck, Ambulance, Search, Wrench, Tag, Sparkles, ChevronLeft, ShieldCheck,
  RefreshCw, Flame, Heart, Shield,
} from 'lucide-react';
import { VehicleImage } from '@/components/game/vehicles/VehicleImage';
import { VEHICLE_IMAGES } from '@/data/assetManifest';
import { vehiclesForStation, VEHICLE_SPEC_LIST, VEHICLE_SPECS, type VehicleSpec } from '@/data/vehicles';
import { stationAllowsEmsVehicles } from '@/data/stations';
import {
  familiesForType,
  moduleByCompositeId,
  moduleIdOf,
  variantOf,
} from '@/data/vehicleModules';
import { formatEuro } from '@/lib/format';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { useGameStore } from '@/state/gameStore';
import { USED_REFRESH_FEE } from '@/data/usedMarket';
import type { Station, UsedVehicleListing, VehicleType } from '@/state/types';

interface VehiclePurchaseDialogProps {
  station: Station | null;
  credits: number;
  onClose: () => void;
  onBuy: (
    station: Station,
    type: VehicleType,
    opts?: {
      name?: string;
      callsign?: string;
      used?: boolean;
      usedKilometers?: number;
      extraModuleIds?: string[];
      removedDefaultFamilies?: string[];
    },
  ) => void;
}

type Tab = 'new' | 'used';
type CategoryFilter = 'all' | 'fire' | 'ems' | 'rescue' | 'police';

interface FilterOption {
  id: CategoryFilter;
  label: string;
}

/**
 * Phase 11.3.1 (Bug 7) — Filter-Set richtet sich nach Wachen-Art.
 * Polizei-Wachen sollen keine Feuerwehr-/Hilfeleistungs-Filter zeigen, etc.
 */
function filterOptionsForKind(kind: string): FilterOption[] {
  if (kind === 'ems-station') {
    return [
      { id: 'all', label: 'Alle' },
      { id: 'ems', label: 'Rettungsdienst' },
    ];
  }
  if (kind === 'police-station' || kind === 'federal-police-station') {
    return [
      { id: 'all', label: 'Alle' },
      { id: 'police', label: 'Streifendienst' },
    ];
  }
  if (kind === 'riot-police-station') {
    return [
      { id: 'all', label: 'Alle' },
      { id: 'police', label: 'Bereitschaft' },
    ];
  }
  if (kind === 'sek-base') {
    return [
      { id: 'all', label: 'Alle' },
      { id: 'police', label: 'Spezialeinheit' },
    ];
  }
  // FF / BF / fallback
  return [
    { id: 'all', label: 'Alle' },
    { id: 'fire', label: 'Feuerwehr' },
    { id: 'rescue', label: 'Hilfeleistung' },
    { id: 'ems', label: 'Rettungsdienst' },
  ];
}

/**
 * Phase 8.5 — Vehicle Shop redesign.
 *  - Hero header with station context.
 *  - Tabs: Neu / Gebraucht (Gebraucht = 60 % cost, condition starts at 0.6).
 *  - Filter row: search + category chips.
 *  - Cards grid with cost / extras.
 *  - Step 2: Konfiguration — name + bestätigen.
 */
export function VehiclePurchaseDialog({
  station,
  credits,
  onClose,
  onBuy,
}: VehiclePurchaseDialogProps) {
  useEffect(() => {
    if (!station) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [station, onClose]);

  const [tab, setTab] = useState<Tab>('new');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [configFor, setConfigFor] = useState<{ spec: VehicleSpec; listing?: UsedVehicleListing } | null>(null);
  const [name, setName] = useState('');
  // Phase 11.3.2 (Bug 8) — Pflichtfeld Rufzeichen.
  const [callsign, setCallsign] = useState('');
  // Phase 12.3.1 — Pre-purchase module configuration.
  const [extraModuleIds, setExtraModuleIds] = useState<string[]>([]);
  const [removedDefaultFamilies, setRemovedDefaultFamilies] = useState<string[]>([]);

  // Phase 9.7.3 — used market listings.
  const usedListings = useGameStore((s) => s.usedVehicleListings ?? []);
  const ensureUsedListings = useGameStore((s) => s.ensureUsedListings);
  const refreshUsedListings = useGameStore((s) => s.refreshUsedListings);
  const pushToast = useGameStore((s) => s.pushToast);
  const mode = useGameStore((s) => s.mode);

  useEffect(() => {
    if (!station) return;
    ensureUsedListings();
  }, [station?.id, ensureUsedListings]);

  // Reset all state when the dialog opens for a different station.
  useEffect(() => {
    if (!station) return;
    setTab('new');
    setSearch('');
    setFilter('all');
    setConfigFor(null);
    setName('');
    setCallsign('');
    setExtraModuleIds([]);
    setRemovedDefaultFamilies([]);
  }, [station?.id]);

  // Reset module config when picking a different vehicle to configure.
  useEffect(() => {
    setExtraModuleIds([]);
    setRemovedDefaultFamilies([]);
  }, [configFor?.spec.type]);

  const allowsEms = useMemo(
    () => (station ? stationAllowsEmsVehicles(station) : false),
    [station],
  );

  // Vehicles available at this station: base + EMS extension addon.
  const allSpecs = useMemo(() => {
    if (!station) return [] as VehicleSpec[];
    const base = vehiclesForStation(station.kind);
    if ((station.kind === 'ff-station' || station.kind === 'bf-station') && allowsEms) {
      const ems = VEHICLE_SPEC_LIST.filter((v) => v.category === 'ems');
      const ids = new Set(base.map((v) => v.type));
      for (const e of ems) if (!ids.has(e.type)) base.push(e);
    }
    return base.sort((a, b) => a.cost - b.cost);
  }, [station, allowsEms]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allSpecs.filter((s) => {
      // Phase 11.3.1 — UI filter "rescue" maps to fire/HLF/RW; "police" → category police.
      if (filter !== 'all') {
        if (filter === 'rescue') {
          if (!(s.category === 'fire' && (s.type === 'HLF' || s.type === 'RW' || s.type === 'GW' || s.type === 'SW'))) {
            return false;
          }
        } else if (filter !== s.category) {
          return false;
        }
      }
      if (q) {
        return (
          s.label.toLowerCase().includes(q) ||
          s.fullName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [allSpecs, search, filter]);

  // Filter used listings by station compatibility (same gating as new specs).
  // Hook must be unconditional, so call it before the early return below.
  const compatibleUsedListings = useMemo(() => {
    if (!station) return [] as UsedVehicleListing[];
    const allowedTypes = new Set(allSpecs.map((s) => s.type));
    return usedListings.filter((l) => allowedTypes.has(l.type));
  }, [usedListings, allSpecs, station]);

  if (!station) return null;

  const isUsed = tab === 'used';
  const costFactor = isUsed ? 0.6 : 1.0;

  const handleBuy = () => {
    if (!configFor) return;
    // Phase 11.3.2 (Bug 8) — name + callsign sind Pflichtfelder.
    const trimmedName = name.trim();
    const trimmedCall = callsign.trim();
    if (!trimmedName || trimmedCall.length < 3) return;
    onBuy(station, configFor.spec.type, {
      name: trimmedName,
      callsign: trimmedCall,
      used: isUsed,
      usedKilometers: configFor.listing?.totalKilometers,
      extraModuleIds: extraModuleIds.length > 0 ? extraModuleIds : undefined,
      removedDefaultFamilies:
        removedDefaultFamilies.length > 0 ? removedDefaultFamilies : undefined,
    });
    setConfigFor(null);
    setName('');
    setCallsign('');
    setExtraModuleIds([]);
    setRemovedDefaultFamilies([]);
  };

  const handleRefresh = () => {
    if (mode !== 'sandbox' && credits < USED_REFRESH_FEE) {
      pushToast({
        kind: 'error',
        text: `Erneuern kostet ${USED_REFRESH_FEE} € — nicht genug Credits.`,
      });
      return;
    }
    const ok = refreshUsedListings(true);
    if (ok) {
      pushToast({
        kind: 'info',
        text:
          mode === 'sandbox'
            ? 'Gebrauchtmarkt erneuert.'
            : `Gebrauchtmarkt erneuert (-${USED_REFRESH_FEE} €).`,
      });
    }
  };

  return (
    <ModalRoot
      onClose={onClose}
      zIndex={Z_MODALS}
      contentClass="w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-lg border border-border-strong bg-bg-secondary"
    >
      {/* Phase 11.3.3 (N6) — Header-Icon spiegelt die Wachen-Art. */}
      <header
        className="relative px-6 py-5 text-white"
        style={{
          background:
            station.kind === 'police-station' ||
            station.kind === 'riot-police-station' ||
            station.kind === 'sek-base' ||
            station.kind === 'federal-police-station'
              ? 'linear-gradient(90deg, #1d4ed8, #1e40af)'
              : station.kind === 'ems-station'
                ? 'linear-gradient(90deg, #f97316, #ea580c)'
                : 'linear-gradient(90deg, #dc2626, #b91c1c)',
        }}
      >
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
            {station.kind === 'police-station' ||
            station.kind === 'riot-police-station' ||
            station.kind === 'sek-base' ||
            station.kind === 'federal-police-station' ? (
              <Shield className="h-6 w-6" aria-hidden />
            ) : station.kind === 'ems-station' ? (
              <Heart className="h-6 w-6" aria-hidden />
            ) : (
              <Flame className="h-6 w-6" aria-hidden />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest opacity-80">Fahrzeug-Shop</p>
            <h3 className="font-display text-2xl font-bold">{station.name}</h3>
            <p className="mt-0.5 text-xs opacity-80">
              Verfügbar: <span className="font-semibold">{formatEuro(credits)}</span>
              {(station.kind === 'ff-station' || station.kind === 'bf-station') && (
                <>
                  {' · '}
                  {allowsEms ? 'Rettungsfahrzeuge verfügbar' : 'Nur Feuerwehr (kein RW-Anbau)'}
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white/15 transition hover:bg-white/25"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </header>

      {configFor ? (
        /* ──── Step 2: Konfiguration ──── */
        <div className="flex max-h-[70vh] flex-col">
          <div className="border-b border-border-subtle bg-bg-secondary px-6 py-3">
            <button
              type="button"
              onClick={() => setConfigFor(null)}
              className="flex items-center gap-1.5 text-xs font-semibold text-fg-muted transition hover:text-fg-primary"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              Zurück zur Auswahl
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <ConfiguratorStep
              spec={configFor.spec}
              listing={configFor.listing}
              station={station}
              isUsed={isUsed}
              name={name}
              onNameChange={setName}
              callsign={callsign}
              onCallsignChange={setCallsign}
              extraModuleIds={extraModuleIds}
              onExtraModulesChange={setExtraModuleIds}
              removedDefaultFamilies={removedDefaultFamilies}
              onRemovedDefaultsChange={setRemovedDefaultFamilies}
              credits={credits}
              onConfirm={handleBuy}
            />
          </div>
        </div>
      ) : (
        /* ──── Step 1: Auswahl ──── */
        <div className="flex max-h-[78vh] flex-col">
          {/* Tabs */}
          <div className="flex border-b border-border-subtle bg-bg-secondary px-6 pt-3">
            <TabButton active={tab === 'new'} onClick={() => setTab('new')} icon={Sparkles}>
              Neu
            </TabButton>
            <TabButton active={tab === 'used'} onClick={() => setTab('used')} icon={Tag}>
              Gebraucht ‑40 %
            </TabButton>
          </div>

          {/* Filter row */}
          <div className="space-y-2 border-b border-border-subtle bg-bg-secondary px-6 py-3">
            <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated/40 px-3 py-2">
              <Search className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Fahrzeug suchen…"
                className="w-full bg-transparent text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none"
              />
            </div>
            {/* Phase 11.3.1 (Bug 7) — station-kind-aware filter set. */}
            <div className="flex flex-wrap gap-1.5">
              {filterOptionsForKind(station.kind).map((opt) => (
                <FilterChip
                  key={opt.id}
                  active={filter === opt.id}
                  onClick={() => setFilter(opt.id)}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </div>
          </div>

          {/* Cards */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isUsed ? (
              compatibleUsedListings.length === 0 ? (
                <div className="rounded-md border border-dashed border-border-subtle py-8 text-center text-sm text-fg-muted">
                  Aktuell keine passenden Gebrauchtwagen verfügbar.
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="mx-auto mt-3 flex items-center gap-1.5 rounded-md bg-bg-elevated px-3 py-1.5 text-xs font-semibold text-fg-primary transition hover:bg-bg-elevated/70"
                  >
                    <RefreshCw className="h-3 w-3" aria-hidden /> Erneuern (50 €)
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={handleRefresh}
                      className="flex items-center gap-1.5 rounded-md bg-bg-elevated px-3 py-1.5 text-[11px] font-semibold text-fg-secondary transition hover:bg-bg-elevated/70"
                    >
                      <RefreshCw className="h-3 w-3" aria-hidden /> Erneuern (50 €)
                    </button>
                  </div>
                  <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {compatibleUsedListings.map((listing) => {
                      const spec = VEHICLE_SPECS[listing.type];
                      const affordable = credits >= listing.cost;
                      return (
                        <li key={listing.id}>
                          <UsedListingCard
                            spec={spec}
                            listing={listing}
                            affordable={affordable}
                            onChoose={() => {
                              setConfigFor({ spec, listing });
                              setName('');
                            }}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </>
              )
            ) : filtered.length === 0 ? (
              <p className="rounded-md border border-dashed border-border-subtle py-8 text-center text-sm text-fg-muted">
                Keine Fahrzeuge passen zu Filter und Suche.
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {filtered.map((spec) => {
                  const cost = Math.round(spec.cost * costFactor);
                  const affordable = credits >= cost;
                  return (
                    <li key={spec.type}>
                      <VehicleCard
                        spec={spec}
                        cost={cost}
                        isUsed={isUsed}
                        affordable={affordable}
                        onChoose={() => {
                          setConfigFor({ spec });
                          setName('');
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </ModalRoot>
  );
}

/* ──────────────── Step 1 cards ──────────────── */

function VehicleCard({
  spec,
  cost,
  isUsed,
  affordable,
  onChoose,
}: {
  spec: VehicleSpec;
  cost: number;
  isUsed: boolean;
  affordable: boolean;
  onChoose: () => void;
}) {
  const Icon = spec.category === 'ems' ? Ambulance : Truck;
  const extras: string[] = [];
  if (spec.waterTankMax > 0) extras.push(`${spec.waterTankMax.toLocaleString('de-DE')} l Wasser`);
  if (spec.breathingAirMax > 0) {
    extras.push(`${spec.breathingAirMax.toLocaleString('de-DE')} l Atemluft`);
  }
  if (spec.speedMultiplier !== 1.0) {
    extras.push(`${spec.speedMultiplier.toFixed(1).replace('.', ',')}× Geschw.`);
  }

  return (
    <button
      type="button"
      onClick={onChoose}
      disabled={!affordable}
      className={[
        'flex w-full flex-col rounded-lg border p-4 text-left transition',
        affordable
          ? 'border-border-subtle bg-bg-elevated/40 hover:border-accent-emergency hover:bg-bg-elevated'
          : 'cursor-not-allowed border-border-subtle bg-bg-elevated/20 opacity-60',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        {/* Phase 12.2.2 — Vehicle image instead of generic icon when available. */}
        {VEHICLE_IMAGES[spec.type] ? (
          <span
            className={[
              'flex h-12 w-16 flex-shrink-0 items-center justify-center rounded-md overflow-hidden',
              'bg-bg-elevated',
            ].join(' ')}
          >
            <VehicleImage
              type={spec.type}
              className="h-full w-full object-contain"
              silhouetteProps={{ className: 'h-8 w-auto text-fg-primary' }}
            />
          </span>
        ) : (
          <span
            className={[
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md',
              spec.category === 'ems'
                ? 'bg-status-transport/15 text-status-transport'
                : 'bg-bg-elevated text-fg-primary',
            ].join(' ')}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-bold">{spec.label}</p>
          <p className="text-[11px] text-fg-muted">{spec.fullName}</p>
        </div>
        {isUsed && (
          <span className="flex-shrink-0 rounded-full bg-amber-500/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-200">
            ‑40 %
          </span>
        )}
      </div>

      <p className="mt-2 line-clamp-2 text-xs text-fg-secondary">{spec.description}</p>

      {extras.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {extras.map((e) => (
            <span
              key={e}
              className="rounded-full bg-bg-primary px-2 py-0.5 text-[10px] text-fg-muted"
            >
              {e}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-end justify-between">
        <div>
          <p className="font-display text-lg font-bold tabular-nums text-accent-emergency">
            {formatEuro(cost)}
          </p>
          <p className="text-[10px] text-fg-muted">
            -{formatEuro(spec.upkeepPerDay)}/Tag Unterhalt
          </p>
        </div>
        <span
          className={[
            'rounded-md px-3 py-1.5 text-[11px] font-semibold',
            affordable ? 'bg-accent-emergency/15 text-accent-emergency' : 'bg-bg-elevated text-fg-muted',
          ].join(' ')}
        >
          {affordable ? 'Konfigurieren →' : 'Credits fehlen'}
        </span>
      </div>
    </button>
  );
}

function UsedListingCard({
  spec,
  listing,
  affordable,
  onChoose,
}: {
  spec: VehicleSpec;
  listing: UsedVehicleListing;
  affordable: boolean;
  onChoose: () => void;
}) {
  const Icon = spec.category === 'ems' ? Ambulance : Truck;
  return (
    <button
      type="button"
      onClick={onChoose}
      disabled={!affordable}
      className={[
        'flex w-full flex-col rounded-lg border p-4 text-left transition',
        affordable
          ? 'border-amber-400/40 bg-amber-500/5 hover:border-amber-400 hover:bg-amber-500/10'
          : 'cursor-not-allowed border-border-subtle bg-bg-elevated/20 opacity-60',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span
          className={[
            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md',
            spec.category === 'ems'
              ? 'bg-status-transport/15 text-status-transport'
              : 'bg-bg-elevated text-fg-primary',
          ].join(' ')}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-bold">{spec.label}</p>
          <p className="text-[11px] text-fg-muted">{spec.fullName}</p>
        </div>
        <span className="flex-shrink-0 rounded-full bg-amber-500/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-200">
          Gebraucht
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-fg-muted">
        <div>
          <span className="block text-fg-secondary">Zustand</span>
          <span className="font-semibold tabular-nums text-fg-primary">
            {Math.round(listing.condition * 100)} %
          </span>
        </div>
        <div>
          <span className="block text-fg-secondary">Laufleistung</span>
          <span className="font-semibold tabular-nums text-fg-primary">
            {listing.totalKilometers.toLocaleString('de-DE')} km
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div>
          <p className="font-display text-lg font-bold tabular-nums text-amber-300">
            {formatEuro(listing.cost)}
          </p>
          <p className="text-[10px] text-fg-muted">
            -{formatEuro(spec.upkeepPerDay)}/Tag
          </p>
        </div>
        <span
          className={[
            'rounded-md px-3 py-1.5 text-[11px] font-semibold',
            affordable
              ? 'bg-amber-500/20 text-amber-200'
              : 'bg-bg-elevated text-fg-muted',
          ].join(' ')}
        >
          {affordable ? 'Konfigurieren →' : 'Credits fehlen'}
        </span>
      </div>
    </button>
  );
}

/* ──────────────── Step 2 configurator ──────────────── */

function ConfiguratorStep({
  spec,
  listing,
  station,
  isUsed,
  name,
  onNameChange,
  callsign,
  onCallsignChange,
  extraModuleIds,
  onExtraModulesChange,
  removedDefaultFamilies,
  onRemovedDefaultsChange,
  credits,
  onConfirm,
}: {
  spec: VehicleSpec;
  listing?: UsedVehicleListing;
  station: Station;
  isUsed: boolean;
  name: string;
  onNameChange: (v: string) => void;
  callsign: string;
  onCallsignChange: (v: string) => void;
  extraModuleIds: string[];
  onExtraModulesChange: (ids: string[]) => void;
  removedDefaultFamilies: string[];
  onRemovedDefaultsChange: (fams: string[]) => void;
  credits: number;
  onConfirm: () => void;
}) {
  // Listing-driven pricing if present (per-listing cost may differ slightly).
  const baseCost = listing ? listing.cost : Math.round(spec.cost * (isUsed ? 0.6 : 1));
  // Phase 12.3.1 — Extra modules cost their full spec cost (no surcharge for pre-purchase).
  const extraCost = extraModuleIds.reduce((acc, id) => {
    const m = moduleByCompositeId(id);
    return acc + (m?.variant.cost ?? 0);
  }, 0);
  const cost = baseCost + extraCost;
  const affordable = credits >= cost;
  const Icon = spec.category === 'ems' ? Ambulance : Truck;
  // Phase 11.3.2 — Pflichtfeld-Validierung.
  const trimmedName = name.trim();
  const trimmedCall = callsign.trim();
  const nameOk = trimmedName.length > 0;
  const callOk = trimmedCall.length >= 3;
  // Slot accounting: defaults occupy slots minus those the user explicitly removed.
  const activeDefaults = (spec.defaultModules ?? []).filter(
    (dm) => !removedDefaultFamilies.includes(dm.family),
  );
  const usedDefaultSlots = activeDefaults.reduce((acc, dm) => {
    const v = variantOf(dm.family, dm.size);
    return acc + (v?.slots ?? 0);
  }, 0);
  const usedExtraSlots = extraModuleIds.reduce((acc, id) => {
    const m = moduleByCompositeId(id);
    return acc + (m?.variant.slots ?? 0);
  }, 0);
  const totalUsedSlots = usedDefaultSlots + usedExtraSlots;
  const availableSlots = Math.max(0, spec.moduleSlots - totalUsedSlots);
  const canConfirm = affordable && nameOk && callOk;

  // Phase 13.2 — Show ALL compatible families. A "duplicate" install of the
  // same family (e.g. a second arrest-equipment) is harmless: the player
  // already sees the default in the Standard-Module section above and can
  // remove it from there, or stack additional variants if slots permit.
  const eligibleFamilies = familiesForType(spec.type);

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4">
        <span
          className={[
            'flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg',
            spec.category === 'ems'
              ? 'bg-status-transport/15 text-status-transport'
              : 'bg-bg-elevated text-fg-primary',
          ].join(' ')}
        >
          <Icon className="h-7 w-7" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">
            Konfiguration · {isUsed ? 'Gebraucht' : 'Neu'}
          </p>
          <p className="font-display text-xl font-bold">{spec.label}</p>
          <p className="text-xs text-fg-muted">{spec.fullName}</p>
        </div>
      </div>

      {/* Phase 11.3.2 (Bug 8) — Name + Rufzeichen sind Pflichtfelder. */}
      <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          Name <span className="text-accent-emergency">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={`Default: ${spec.label}`}
          maxLength={40}
          className={[
            'mt-2 w-full rounded-md border bg-bg-primary px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none',
            nameOk ? 'border-border-subtle focus:border-accent-emergency' : 'border-accent-emergency/60',
          ].join(' ')}
        />
        <p className="mt-1 text-[10px] text-fg-muted">Anzeige-Name in der Fahrzeugliste.</p>

        <label className="mt-4 block text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          Rufzeichen <span className="text-accent-emergency">*</span>
        </label>
        <input
          type="text"
          value={callsign}
          onChange={(e) => onCallsignChange(e.target.value)}
          placeholder={`z. B. „Florian ${station.name.split(' ')[0]} 1/40-1"`}
          maxLength={48}
          className={[
            'mt-2 w-full rounded-md border bg-bg-primary px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none',
            callOk ? 'border-border-subtle focus:border-accent-emergency' : 'border-accent-emergency/60',
          ].join(' ')}
        />
        <p className="mt-1 text-[10px] text-fg-muted">
          Mindestens 3 Zeichen — wird im Funk angezeigt.
        </p>
      </div>

      {/* Phase 12.3.1 — Pre-purchase module configuration. */}
      <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          <Wrench className="h-3 w-3" aria-hidden />
          Module · {totalUsedSlots} / {spec.moduleSlots} Slots belegt
        </p>
        <p className="mb-3 text-[11px] text-amber-300">
          💡 Erstkonfiguration ohne Aufschlag — späterer Tausch +50 % und 5 min außer Dienst.
        </p>

        {/* Default modules list */}
        {activeDefaults.length > 0 && (
          <div className="mb-3">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-fg-muted">
              Standard-Module (inkl.)
            </p>
            <ul className="space-y-1.5">
              {activeDefaults.map((dm) => {
                const v = variantOf(dm.family, dm.size);
                const fam = v ? moduleByCompositeId(moduleIdOf(dm.family, dm.size))?.family : null;
                return (
                  <li
                    key={`def-${dm.family}-${dm.size ?? ''}`}
                    className="flex items-center justify-between rounded-md border border-status-available/40 bg-status-available/5 px-2.5 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold">
                        {fam?.name ?? dm.family}
                        {v?.label ? <span className="ml-1 text-fg-muted">· {v.label}</span> : null}
                      </p>
                      <p className="text-[10px] text-status-available">Inkl. — kostenlos</p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        onRemovedDefaultsChange([...removedDefaultFamilies, dm.family])
                      }
                      className="rounded-md bg-bg-elevated px-2 py-0.5 text-[10px] font-semibold text-fg-muted transition hover:text-accent-emergency"
                    >
                      Entfernen
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Extra modules list */}
        {extraModuleIds.length > 0 && (
          <div className="mb-3">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-fg-muted">
              Extra-Module
            </p>
            <ul className="space-y-1.5">
              {extraModuleIds.map((id, i) => {
                const m = moduleByCompositeId(id);
                if (!m) return null;
                return (
                  <li
                    key={`extra-${id}-${i}`}
                    className="flex items-center justify-between rounded-md border border-accent-dispatch/40 bg-accent-dispatch/5 px-2.5 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold">
                        {m.family.name}
                        {m.variant.label ? (
                          <span className="ml-1 text-fg-muted">· {m.variant.label}</span>
                        ) : null}
                      </p>
                      <p className="text-[10px] text-accent-dispatch">
                        +{formatEuro(m.variant.cost)} · {m.variant.slots} Slot
                        {m.variant.slots === 1 ? '' : 's'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        onExtraModulesChange(extraModuleIds.filter((_, j) => j !== i))
                      }
                      className="rounded-md bg-bg-elevated px-2 py-0.5 text-[10px] font-semibold text-fg-muted transition hover:text-accent-emergency"
                    >
                      Entfernen
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Add-extra picker */}
        {availableSlots > 0 ? (
          <details className="rounded-md border border-border-subtle bg-bg-primary/40 p-2">
            <summary className="cursor-pointer text-[11px] font-semibold text-fg-secondary">
              + Modul hinzufügen ({availableSlots} Slot
              {availableSlots === 1 ? '' : 's'} frei)
            </summary>
            <div className="mt-2 max-h-48 space-y-2 overflow-y-auto">
              {eligibleFamilies.map((fam) => (
                <div key={fam.family} className="rounded-md border border-border-subtle p-2">
                  <p className="text-[11px] font-semibold">{fam.name}</p>
                  <p className="text-[10px] text-fg-muted">{fam.description}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {fam.variants.map((v) => {
                      const id = moduleIdOf(fam.family, v.size);
                      const tooBig = v.slots > availableSlots;
                      return (
                        <button
                          key={id}
                          type="button"
                          disabled={tooBig}
                          onClick={() => onExtraModulesChange([...extraModuleIds, id])}
                          className={[
                            'rounded-md px-2 py-1 text-[10px] font-semibold transition',
                            tooBig
                              ? 'cursor-not-allowed bg-bg-elevated text-fg-muted opacity-50'
                              : 'bg-accent-dispatch/15 text-accent-dispatch hover:bg-accent-dispatch/25',
                          ].join(' ')}
                        >
                          {v.label ?? 'Standard'} · {formatEuro(v.cost)}
                          {v.slots > 1 ? ` · ${v.slots} slots` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </details>
        ) : (
          <p className="text-[11px] text-fg-muted">Alle Slots belegt.</p>
        )}
      </div>

      <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Stat label="Fahrzeug" value={formatEuro(baseCost)} tone="warn" />
          <Stat label="Module" value={formatEuro(extraCost)} />
          <Stat label="Gesamt" value={formatEuro(cost)} tone="warn" />
          <Stat label="Wartung/Tag" value={`-${formatEuro(spec.upkeepPerDay)}`} />
          {isUsed && (
            <>
              <Stat
                label="Startkondition"
                value={`${Math.round((listing?.condition ?? 0.6) * 100)} %`}
                tone="warn"
              />
              <Stat label="Ersparnis" value={formatEuro(spec.cost - baseCost)} tone="ok" />
              {listing && (
                <Stat
                  label="Kilometerstand"
                  value={`${listing.totalKilometers.toLocaleString('de-DE')} km`}
                />
              )}
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onConfirm}
        disabled={!canConfirm}
        className={[
          'flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-bold transition',
          canConfirm
            ? 'bg-accent-emergency text-white shadow-lg hover:bg-accent-emergency-hover'
            : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
        ].join(' ')}
      >
        <ShieldCheck className="h-4 w-4" aria-hidden />
        {!nameOk
          ? 'Name fehlt'
          : !callOk
            ? 'Rufzeichen fehlt (≥ 3 Zeichen)'
            : affordable
          ? `Kaufen für ${formatEuro(cost)}`
          : `Nicht genug Credits (${formatEuro(credits)} / ${formatEuro(cost)})`}
      </button>
    </div>
  );
}

/* ──────────────── helpers ──────────────── */

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Truck;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex items-center gap-1.5 px-4 py-2 text-xs font-semibold transition',
        active
          ? 'border-b-2 border-accent-emergency text-fg-primary'
          : 'border-b-2 border-transparent text-fg-muted hover:text-fg-secondary',
      ].join(' ')}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {children}
    </button>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full px-3 py-1 text-[10px] font-semibold transition',
        active
          ? 'bg-accent-emergency text-white'
          : 'border border-border-subtle bg-bg-elevated text-fg-muted hover:border-border-strong hover:text-fg-secondary',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
}) {
  const toneClass =
    tone === 'ok'
      ? 'text-status-available'
      : tone === 'warn'
        ? 'text-accent-emergency'
        : 'text-fg-primary';
  return (
    <div className="rounded-md border border-border-subtle bg-bg-primary/50 p-2.5">
      <p className="text-[9px] uppercase tracking-wider text-fg-muted">{label}</p>
      <p className={['mt-0.5 font-display text-base font-bold tabular-nums', toneClass].join(' ')}>
        {value}
      </p>
    </div>
  );
}
